import type { StripeAccountName, StripeAddress, StripeStoreProduct } from '@inkitt/contracts-common';
import Stripe from 'stripe';
import { z } from 'zod';
import { c } from '../contract';
import { AppAttribution } from '../schemas';
import type { RawAttribution } from '../schemas/attribution.schema';

export const checkoutRouter = c.router(
  {
    createIntent: {
      summary: 'Create stripe payment intent',
      method: 'POST',
      path: '/intent',
      body: c.type<{
        checkoutEmail: string;
        promoCode?: string;
        stripeStoreProduct: StripeStoreProduct;
        language: string;
        paymentMethodTypes?: string[];
        stripeAccount: StripeAccountName;
        subscriptionStartDateMillis?: number;
        // These are explicitly set to nullable to ensure they're not accidentally missed from the client
        adjustWebUuid: string | null;
        webAttribution: RawAttribution | null;
        freeTrialDays?: number;
        address: StripeAddress | null;
        product?: 'harlequin';
        referrerSource?: 'harlequin';
        utmSource?: string;
      }>(),
      responses: {
        200: c.type<{
          clientSecret?: string;
          paymentIntentId?: string;
          secureUserId?: string;
          newStripeCustomerId?: string;
          freeTrialData?: {
            subscriptionId: string;
            subscriptionStatus: string;
          };
          delayedSubscriptionData?: {
            scheduleId: string;
          };
        }>(),
        409: c.type<{ reason: 'already_subscribed' }>(),
        422: z.literal('Unprocessable Entity'),
        500: c.type<{ error: string }>(),
      },
    },
    createAppToWebIntent: {
      summary: 'Create stripe payment intent by userId for app flow',
      method: 'POST',
      path: '/intent/user-id',
      body: c.type<{
        checkoutUserId: string;
        checkoutEmail?: string;
        stripeStoreProduct: StripeStoreProduct;
        introOfferProduct?: StripeStoreProduct;
        language: string;
        promoCode?: string;
        appAttribution: undefined | AppAttribution;
        freeTrialDays?: number;
        upgradeSubscriptionId?: string;
        stripeAccount: StripeAccountName;
        subscriptionStartDateMillis?: number;
        storyAttribution: Partial<{
          seriesId: string;
          episodeId: string;
          testBranch: string;
          metadataTestVariantId: string;
          metadataTestVariantName: string;
        }> | null;
        address: StripeAddress | null;
        externalTransactionToken?: string;
      }>(),
      responses: {
        200: c.type<{
          clientSecret?: string;
          paymentIntentId?: string;
          secureUserId?: string;
          newStripeCustomerId?: string;
          freeTrialData?: {
            subscriptionId: string;
            subscriptionStatus: string;
          };
          delayedSubscriptionData?: {
            scheduleId: string;
          };
          upgradedSubscriptionData?: {
            subscriptionId: string;
          };
        }>(),
        409: c.type<{ reason: 'already_subscribed' }>(),
        422: z.literal('Unprocessable Entity'),
        500: c.type<{ error: string }>(),
      },
    },
    createEarlySubscriptionRenewalIntent: {
      summary: 'Create stripe invoice for subscription renewal',
      method: 'POST',
      path: '/invoice/subscription-renewal',
      body: c.type<{
        userId: string;
        customerId: string;
        stripeAccount: StripeAccountName;
        coupon?: Stripe.Coupon;
        subscriptionId: string;
        paymentMethodTypes?: string[];
        languageCode: string;
        adjustWebUuid: string | null;
        webAttribution: RawAttribution | null;
      }>(),
      responses: {
        200: z.object({
          paymentIntentId: z.string(),
          clientSecret: z.string().nullable(),
        }),
        422: z.literal('Unprocessable Entity'),
      },
    },
    createCheckoutSession: {
      summary: 'Create stripe checkout session',
      method: 'POST',
      path: '/checkout-session',
      body: c.type<{
        checkoutEmail?: string;
        promoCode?: string;
        stripeStoreProduct: StripeStoreProduct;
        language: string;
        paymentMethodTypes?: Stripe.Checkout.SessionCreateParams.PaymentMethodType[];
        stripeAccount: StripeAccountName;
        // These are explicitly set to nullable to ensure they're not accidentally missed from the client
        adjustWebUuid: string | null;
        webAttribution: RawAttribution | null;
        returnUrl: string;
        freeTrialDays?: number;
        product?: 'harlequin';
        referrerSource?: 'harlequin';
        utmSource?: string;
      }>(),
      responses: {
        200: c.type<{ clientSecret: string }>(),
        409: c.type<{ reason: 'already_subscribed' }>(),
        422: z.literal('Unprocessable Entity'),
        500: c.type<{ error: string }>(),
      },
    },
    createAppToWebCheckoutSession: {
      summary: 'Create stripe checkout session by userId for app flow',
      method: 'POST',
      path: '/checkout-session/user-id',
      body: c.type<{
        checkoutUserId: string;
        checkoutEmail?: string;
        stripeStoreProduct: StripeStoreProduct;
        introOfferProduct?: StripeStoreProduct;
        language: string;
        promoCode?: string;
        appAttribution: undefined | AppAttribution;
        paymentMethodTypes?: Stripe.Checkout.SessionCreateParams.PaymentMethodType[];
        storyAttribution: Partial<{
          seriesId: string;
          episodeId: string;
          testBranch: string;
          metadataTestVariantId: string;
          metadataTestVariantName: string;
        }> | null;
        stripeAccount: StripeAccountName;
        returnUrl: string;
        freeTrialDays?: number;
        upgradeSubscriptionId?: string;
        externalTransactionToken?: string;
      }>(),
      responses: {
        200: c.type<{ clientSecret: string }>(),
        409: c.type<{ reason: 'already_subscribed' }>(),
        422: z.literal('Unprocessable Entity'),
        500: c.type<{ error: string }>(),
      },
    },
  },
  { pathPrefix: '/checkout' },
);
